/* eslint-disable react/jsx-no-useless-fragment */
import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Box,
  InputAdornment,
  TextField,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Button,
  Typography,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import searchIcon from "packages/novous_web/src/assets/icons/Icon.svg";
import filterIcon from "packages/novous_web/src/assets/icons/Filter.svg";
import useAxios from "packages/novous_web/src/api/useAxios";
import { API_ENDPOINTS } from "packages/novous_web/src/api/endpoints";
import { useAuth } from "packages/novous_web/src/authContext/useAuth";
import AlertToast from "../../../../common/components/Alert"; // Import your AlertToast component
import { AlertColor } from "@mui/material/Alert";

interface Vehicle {
  regNo: string;
  name: string;
  selected: boolean;
}

interface AddGroupVehiclesModalProps {
  open: boolean;
  onClose: () => void;
  onAddVehicles: (selectedVehicles: Vehicle[]) => void;
  groupData: any;
}

const AddGroupVehicles: React.FC<AddGroupVehiclesModalProps> = ({
  open,
  onClose,
  onAddVehicles,
  groupData,
}) => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState<AlertColor>("info");
  const pageSize = 10;
  const theme = useTheme();
  const tableHeaderBg = theme.palette.mode === "dark" ? "#2D2D2D" : "#F0EFF8";

  const { data: vehicleData, fetchData: fetchVehicleData } = useAxios<{
    data: any[];
  }>(API_ENDPOINTS.directories.GET_ALL_VEHICLES, "POST");

  const { data: addVehicleData, fetchData: fetchAddVehicleData } = useAxios<{
    data: any[];
  }>(API_ENDPOINTS.directories.GROUP_EDIT, "POST");

  const {
    data: allGroupData,
    error: groupError,
    loading: groupLoading,
    fetchData: fetchGroupData,
  } = useAxios<any>(
    `${API_ENDPOINTS.directories.GET_ALL_GROUP}${user?.customer_id}`,
    "GET"
  );

  const usedVehicleRegNos = useMemo(() => {
    const allUsed = new Set<string>();
    allGroupData?.data?.forEach((group: any) => {
      group.vehicleList?.forEach((regNo: string) => {
        allUsed.add(regNo);
      });
    });
    return allUsed;
  }, [allGroupData]);

  useEffect(() => {
    if (user?.customer_id) {
      fetchVehicleData({ fleetId: user.customer_id });
      fetchGroupData();
    }
  }, [user?.customer_id]);

  useEffect(() => {
    if (addVehicleData) {
      setToastMessage("Vehicles added successfully!");
      setToastType("success");
      setToastOpen(true);

      // Get selected vehicles
      const selectedVehicles = vehicles.filter((vehicle) => vehicle.selected);

      // Wait for the toast to be shown for 2 seconds before closing the modal
      setTimeout(() => {
        setToastOpen(false); // Close the toast
        onAddVehicles(selectedVehicles); // Notify parent to refresh data
        onClose(); // Close the modal
      }, 2000);
    }
  }, [addVehicleData]);

  const handleSelectVehicle = (regNo: string) => {
    setVehicles(
      vehicles.map((vehicle) =>
        vehicle.regNo === regNo
          ? { ...vehicle, selected: !vehicle.selected }
          : vehicle
      )
    );
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  const handleAddVehicles = async () => {
    const selectedVehicles = vehicles.filter((vehicle) => vehicle.selected);
    const payload = {
      fleetManagerId: groupData?.fleetManagerId,
      groupName: groupData?.groupName,
      groupDescription: groupData?.groupDescription,
      vehicleList: selectedVehicles.map((vehicle) => vehicle.regNo),
    };

    try {
      await fetchAddVehicleData(payload);
    } catch (error) {
      setToastMessage("Failed to add vehicles.");
      setToastType("error");
      setToastOpen(true);

      setTimeout(() => {
        setToastOpen(false);
      }, 2000);
    }
  };

  useEffect(() => {
    if (vehicleData?.data) {
      const mappedVehicles: Vehicle[] = vehicleData.data.map((item) => {
        const isSelected = groupData?.vehicleList.some(
          (vehicle: any) => vehicle === item.vehicleRegistrationNo
        );
        return {
          regNo: item.vehicleRegistrationNo,
          name: item.vehicleName || "NA",
          selected: isSelected,
        };
      });
      setVehicles(mappedVehicles);
    }
  }, [vehicleData, groupData?.vehicleList]);

  const filteredVehicles = vehicles.filter(
    (vehicle) =>
      vehicle.regNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vehicle.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const paginatedVehicles = filteredVehicles.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const totalPages = Math.ceil(filteredVehicles.length / pageSize);
  const isAddDisabled = !vehicles.some((vehicle) => vehicle.selected);

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "80%",
            bgcolor: "background.paper",
            borderRadius: "8px",
            p: 3,
            boxShadow: 24,
            maxHeight: "100vh",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <Typography
              variant="h5"
              fontWeight={600}
              sx={{
                color: theme.palette.mode === "dark" ? "#FFFFFF" : "#000000",
              }}
            >
              Add Vehicles
            </Typography>

            <TextField
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search"
              sx={{ width: 320 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img src={searchIcon} alt="search" width={16} height={16} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <TableContainer component={Paper} sx={{ maxHeight: "50vh" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: tableHeaderBg }}>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: tableHeaderBg }}>
                    Vehicle Reg No.
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: tableHeaderBg }}>
                    Vehicle Name
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, backgroundColor: tableHeaderBg }}
                    align="center"
                  >
                    Select
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedVehicles.map((vehicle, index) => (
                  <TableRow
                    key={vehicle.regNo}
                    sx={{
                      backgroundColor:
                        index % 2 === 0 ? tableHeaderBg : "inherit",
                    }}
                  >
                    <TableCell sx={{ fontWeight: 400, fontSize: "14px" }}>
                      {vehicle.regNo}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 400, fontSize: "14px" }}>
                      {vehicle.name}
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={vehicle.selected}
                        onChange={() => handleSelectVehicle(vehicle.regNo)}
                        disabled={usedVehicleRegNos.has(vehicle.regNo)}
                        sx={{
                          color: "#5547DD",
                          "&.Mui-checked": {
                            color: "#5547DD",
                          },
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: theme.palette.mode === "dark" ? "#FFFFFF" : "#000000",
                },
                "& .Mui-selected": {
                  backgroundColor: "#5547DD !important",
                  color: "#FFFFFF",
                },
              }}
            />
          </Box>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "16px",
            }}
          >
            <Button onClick={onClose} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleAddVehicles}
              variant="contained"
              disabled={isAddDisabled}
            >
              Add Vehicles
            </Button>
          </div>

          {/* Toast Alert shifted inside the modal */}
          <AlertToast
            message={toastMessage}
            type={toastType}
            open={toastOpen}
            onClose={() => setToastOpen(false)}
          />
        </Box>
      </Modal>
    </>
  );
};

export default AddGroupVehicles;
